import { apiClient } from "./client";
import type {
  AddMemberResponse,
  CallLimit,
  InviteMemberResponse,
  MemberPasswordResetResponse,
  MyProjectRead,
  PermissionCatalogCategory,
  PermissionEntry,
  Project,
  ProjectFeaturesRead,
  ProjectFeaturesUpdate,
  ProjectMember,
  ProjectUpdate,
  Role,
  RoleCreate,
  RoleUpdate,
} from "./types";

export async function listProjects(): Promise<Project[]> {
  const res = await apiClient.get<Project[]>("/projects");
  return res.data;
}

/** The projects the current user is a member of (id + name + slug).
 *  Lighter than `listProjects` and always available — used by the
 *  task-form project picker, which can't rely on the project store
 *  (it isn't persisted across a hard reload). */
export async function getMyProjects(): Promise<MyProjectRead[]> {
  const res = await apiClient.get<MyProjectRead[]>("/me/projects");
  return res.data;
}

export async function getProject(slug: string): Promise<Project> {
  const res = await apiClient.get<Project>(`/projects/${slug}`);
  return res.data;
}

export async function updateProject(
  slug: string,
  payload: ProjectUpdate,
): Promise<Project> {
  const res = await apiClient.patch<Project>(`/projects/${slug}`, payload);
  return res.data;
}

/** Superadmin-only: toggle per-project feature flags. Each field
 *  is a tri-state — omit to leave unchanged, true/false to set. */
export async function updateProjectFeatures(
  slug: string,
  payload: ProjectFeaturesUpdate,
): Promise<ProjectFeaturesRead> {
  const res = await apiClient.patch<ProjectFeaturesRead>(
    `/projects/${slug}/features`,
    payload,
  );
  return res.data;
}

// ─── Members ──────────────────────────────────────────────────────────────

export async function listMembers(
  slug: string,
  params: { q?: string } = {},
): Promise<ProjectMember[]> {
  const cleaned: Record<string, unknown> = {};
  if (params.q && params.q.trim() !== "") cleaned.q = params.q.trim();
  const res = await apiClient.get<ProjectMember[]>(
    `/projects/${slug}/members`,
    { params: cleaned },
  );
  return res.data;
}

export async function resetMemberPassword(
  slug: string,
  userId: string,
): Promise<MemberPasswordResetResponse> {
  const res = await apiClient.post<MemberPasswordResetResponse>(
    `/projects/${slug}/members/${userId}/reset-password`,
  );
  return res.data;
}

export interface InviteMemberPayload {
  email: string;
  full_name: string;
  role_id?: number | null;
  /** Numbers the Call Center may ever hand this member (a salesperson on
   *  trial); omitted or null means no limit. */
  call_limit?: number | null;
}

export async function inviteMember(
  slug: string,
  payload: InviteMemberPayload,
): Promise<InviteMemberResponse> {
  const res = await apiClient.post<InviteMemberResponse>(
    `/projects/${slug}/members/invite`,
    payload,
  );
  return res.data;
}

export interface AddMemberPayload {
  email: string;
  full_name: string;
  role_id?: number | null;
  /** Same as `InviteMemberPayload.call_limit`. */
  call_limit?: number | null;
}

export async function addMember(
  slug: string,
  payload: AddMemberPayload,
): Promise<AddMemberResponse> {
  const res = await apiClient.post<AddMemberResponse>(
    `/projects/${slug}/members/add`,
    payload,
  );
  return res.data;
}

export async function resendMemberInvite(
  slug: string,
  email: string,
): Promise<InviteMemberResponse> {
  const res = await apiClient.post<InviteMemberResponse>(
    `/projects/${slug}/members/resend-invite`,
    { email },
  );
  return res.data;
}

export async function removeMember(
  slug: string,
  userId: string,
): Promise<void> {
  await apiClient.delete(`/projects/${slug}/members/${userId}`);
}

export async function changeMemberRole(
  slug: string,
  userId: string,
  roleId: number,
): Promise<ProjectMember> {
  const res = await apiClient.patch<ProjectMember>(
    `/projects/${slug}/members/${userId}`,
    { role_id: roleId },
  );
  return res.data;
}

/** Set (a number) or remove (null) a member's Call Center allowance. */
export async function setMemberCallLimit(
  slug: string,
  userId: string,
  limit: number | null,
): Promise<CallLimit> {
  const res = await apiClient.put<CallLimit>(
    `/projects/${slug}/members/${userId}/call-limit`,
    { limit },
  );
  return res.data;
}

// ─── Roles + permissions ──────────────────────────────────────────────────

export async function listPermissions(
  slug: string,
): Promise<PermissionEntry[]> {
  const res = await apiClient.get<PermissionEntry[]>(
    `/projects/${slug}/permissions`,
  );
  return res.data;
}

/**
 * Pre-grouped permission catalog used by the role-edit grid. Same data
 * as listPermissions but bucketed by category server-side, so the
 * frontend doesn't have to maintain a static group order — new
 * categories appear automatically as the backend adds them.
 */
export async function listPermissionsCatalog(
  slug: string,
): Promise<PermissionCatalogCategory[]> {
  const res = await apiClient.get<PermissionCatalogCategory[]>(
    `/projects/${slug}/permissions/catalog`,
  );
  return res.data;
}

export async function listRoles(slug: string): Promise<Role[]> {
  const res = await apiClient.get<Role[]>(`/projects/${slug}/roles`);
  return res.data;
}

export async function createRole(
  slug: string,
  payload: RoleCreate,
): Promise<Role> {
  const res = await apiClient.post<Role>(`/projects/${slug}/roles`, payload);
  return res.data;
}

export async function updateRole(
  slug: string,
  roleId: number,
  payload: RoleUpdate,
): Promise<Role> {
  const res = await apiClient.patch<Role>(
    `/projects/${slug}/roles/${roleId}`,
    payload,
  );
  return res.data;
}

export async function deleteRole(
  slug: string,
  roleId: number,
): Promise<void> {
  await apiClient.delete(`/projects/${slug}/roles/${roleId}`);
}
